import { Immutable, toImmutable } from 'nuclear-js';
import moment from 'moment';

import CommonValues from 'resources/common-values';
import CommonStrings from 'resources/common-strings';
import TermUtils from 'utils/terms';

export function findEndTermScore(growthGoals, maxTestEvent, targetTermNumber) {
    const goalForEndTerm = growthGoals ? growthGoals.find((g) => g.get('endTermNumber') === targetTermNumber) : null;
    if (!goalForEndTerm && maxTestEvent && maxTestEvent.get('termNumber') === targetTermNumber) {
        return maxTestEvent.get('score', null);
    } else if (goalForEndTerm) {
        return goalForEndTerm.get('endTermScore', null);
    }

    return null;
}

export function isValidNormsAvailable(measurementScaleID) {
    return ![103, 104, 105, 106, 107, 108, 109, 110].includes(measurementScaleID);
}

export function canSetGrowthGoal(testEvents, firstOpenTerm) {
    if (!testEvents || !firstOpenTerm) {
        return false;
    }
    const firstOpenTermNumber = firstOpenTerm.get('termNumber');
    const academicYearForOpenTerm = TermUtils.findAcademicYearForTermNumber(firstOpenTermNumber);

    // check to see if the test was within 1 year of the first open term
    return testEvents.reduce((currVal, nextTest) => {
        const isCourseSpecific = nextTest.get('isCourseSpecific');
        // if CS
        if (isCourseSpecific) {
            const academicYear = TermUtils.findAcademicYearForTermNumber(nextTest.get('termNumber'));
            return (
                currVal ||
                (academicYear === academicYearForOpenTerm && isValidNormsAvailable(nextTest.get('measurementScaleID')))
            );
        } else {
            return currVal || nextTest.get('termNumber') + 100 >= firstOpenTermNumber;
        }
    }, false);
}

export function isGrowthGoal(goal) {
    return !!goal.get('startTermScore');
}

export function testEventAchievingGoal(goal, testEvents) {
    if (!goal || !testEvents || testEvents.isEmpty()) {
        return null;
    }

    const mostRecentEvent = testEvents.maxBy((te) => te.get('termNumber'));
    return mostRecentEvent.get('score') >= goal.get('targetScore') ? mostRecentEvent : null;
}

/* a term is considered open if:
 *      1. it is not summer
 *      2. the test window end date has not passed
 *      3. it is after the last test event */
export function getFirstOpenTerm(termList, subjectBid, testEvents) {
    if (!termList || !subjectBid) {
        return null;
    }

    const mostRecentTestEventForSubject = testEvents
        ? testEvents.filter((te) => te.get('subject') === subjectBid).maxBy((te) => te.get('termNumber'))
        : null;
    const termOfMostRecentTestEventForSubject = mostRecentTestEventForSubject
        ? mostRecentTestEventForSubject.get('termNumber')
        : 0;
    const dateToUse = moment
        .utc()
        .subtract(1, 'days')
        .valueOf();

    /*
     * When the term you need to test does not match the literal current date,
     * it can be useful (if not necessary) to mock out the current date.
     * const mockTime = 1665439139215; // Oct 10 2022
     * console.log('rewound time', mockTime);
     * console.log('the time lol', new Date(mockTime));
     */

    const openTerms = termList.filter((t) => {
        const isTestWindowEndDateBeforeCurrentTime = t.get('testWindowEndDate') >= dateToUse;

        return (
            t.get('termNumber') % 10 !== 3 && // This just means "is not summer"
            t.get('termNumber') > termOfMostRecentTestEventForSubject && // Is the term more recent than the termOfMostRecentTestEventForSubject?
            isTestWindowEndDateBeforeCurrentTime
        ); // to handle end of term
    });

    return openTerms.isEmpty() ? null : openTerms.minBy((t) => t.get('termNumber'));
}

export const findCurrentGoals = (growthGoals, firstOpenTerm) => {
    if (!growthGoals) {
        return null;
    }

    return firstOpenTerm
        ? growthGoals.filter((g) => {
              return g.get('endTermNumber') >= firstOpenTerm.get('termNumber');
          })
        : null;
};

export function findPastGoals(growthGoals, firstOpenTerm) {
    if (!growthGoals) {
        return null;
    }

    return firstOpenTerm
        ? growthGoals.filter((g) => g.get('endTermNumber') < firstOpenTerm.get('termNumber'))
        : growthGoals;
}

export function goalStatus(goal) {
    //goal cannot be null.
    if (!goal.get('endTermScore')) {
        return CommonValues.goalStatuses.nottested;
    } else if (goal.get('targetScore') > goal.get('endTermScore')) {
        return CommonValues.goalStatuses.notmet;
    } else {
        return CommonValues.goalStatuses.met;
    }
}

export function findGoalSettingTerms(terms, firstOpenTerm, currentTerm, testEvent, studentTermData) {
    if (!terms || !firstOpenTerm || !currentTerm || !studentTermData || studentTermData.isEmpty()) {
        return Immutable.List();
    }

    /* Disable setting Growth Goals for Course Specific in Summer */
    if (testEvent && testEvent.get('isCourseSpecific') === true && currentTerm.get('termNumber') % 10 === 3) {
        return Immutable.List();
    }

    const maxStudentTerm = studentTermData.maxBy((t) => t.get('termNumber')).get('termNumber');

    // if there is a test event and it's within 1 year of first open term, use it
    const maxTerm =
        testEvent && testEvent.get('termNumber') >= firstOpenTerm.get('termNumber') - 100
            ? testEvent.get('termNumber') + 100
            : currentTerm.get('termNumber') + 100;
    const endTerm = Math.min(maxStudentTerm, maxTerm);
    const currentTermAY = TermUtils.findAcademicYearForTermNumber(parseInt(currentTerm.get('termNumber')));
    /**
     * For fall test event - use AY boundaries for setting goal in spring.
     */
    if (testEvent && testEvent.get('isCourseSpecific')) {
        return terms.filter((t) => {
            const candidateTermAY = TermUtils.findAcademicYearForTermNumber(parseInt(t.get('termNumber')));

            return (
                candidateTermAY === currentTermAY &&
                t.get('termNumber') >= firstOpenTerm.get('termNumber') &&
                t.get('termNumber') <= endTerm &&
                t.get('termNumber') % 10 !== 3
                // remove the summer terms
            );
        });
    }

    return terms.filter((t) => {
        return (
            t.get('termNumber') >= firstOpenTerm.get('termNumber') &&
            t.get('termNumber') <= endTerm &&
            t.get('termNumber') % 10 !== 3
        ); // remove the summer terms
    });
}

export function findMaxTestEventForSubject(testEvents, subject) {
    if (!testEvents || !subject) {
        return null;
    }

    const testEventsForSubject = testEvents.filter((te) => te.get('subject') === subject.get('bid'));
    return !testEventsForSubject.isEmpty() ? testEventsForSubject.maxBy((te) => te.get('termNumber')) : null;
}

export function findMaxTestEventForMSID(testEvents, MSID) {
    if (!testEvents || !MSID) {
        return null;
    }

    const testEventsForSubject = testEvents.filter((te) => te.get('measurementScaleID') === MSID);
    return !testEventsForSubject.isEmpty() ? testEventsForSubject.maxBy((te) => te.get('termNumber')) : null;
}

export function findInitialRitTarget(
    growthGoalYn,
    typicalGrowth,
    typicalScore,
    previousRitScore,
    growthGoals,
    targetTermNumber
) {
    let targetArr = [];

    if (!growthGoalYn) {
        return typicalScore ? typicalScore : CommonValues.MIN_RIT;
    }
    // get data from store
    if (growthGoals) {
        growthGoals.forEach((goal) => {
            if (goal.get('endTermNumber') == targetTermNumber) {
                const targetScore = goal.get('targetScore');
                targetArr.push(targetScore);
            }
        });
    }

    if (targetArr.length > 0) {
        return targetArr[0];
    }
    // pick default values
    else {
        if (growthGoalYn && previousRitScore) {
            const growth = typicalGrowth ? typicalGrowth : 1;
            return previousRitScore + growth;
        }
    }

    return null;
}

export function findTypicalGrowthForTargetTerm(growthProjections, targetTermNumber) {
    let typicalGrowth = null;
    let predictedScore = null;
    let percentileIfMet = null;

    if (growthProjections && targetTermNumber) {
        const projectionForTerm = growthProjections.find((p) => p.get('endingTerm') === targetTermNumber);

        if (projectionForTerm && projectionForTerm.get('predictedGrowth')) {
            typicalGrowth = Math.round(projectionForTerm.get('predictedGrowth'));
        }

        if (projectionForTerm && projectionForTerm.get('predictedScore')) {
            predictedScore = Math.round(projectionForTerm.get('predictedScore'));
        }

        if (projectionForTerm && projectionForTerm.get('predictedScorePercentile')) {
            percentileIfMet = Math.round(projectionForTerm.get('predictedScorePercentile'));
        }
    }
    return {
        typicalGrowth,
        predictedScore,
        percentileIfMet,
    };
}

export function findTypicalScore(ritQuintiles, termNumber, subject, studentTermData) {
    if (!ritQuintiles || !termNumber || !subject || !studentTermData) {
        return null;
    }

    const dataForTerm = studentTermData.find((t) => t.get('termNumber') === termNumber);
    if (!dataForTerm) {
        return null;
    }

    const key = toImmutable({
        termNumber,
        subject: subject.get('bid'),
        grade: dataForTerm.get('grade'),
        weeks: dataForTerm.get('weeks'),
    });

    return ritQuintiles.getIn([key, 50], null);
}

export function findPreviousRitScore(maxTestEvent, firstOpenTerm) {
    // if maxTestEvent exists and is within 1 year of firstOpenTerm
    if (maxTestEvent && firstOpenTerm && maxTestEvent.get('termNumber') + 100 >= firstOpenTerm.get('termNumber')) {
        return maxTestEvent.get('score', null);
    }

    return null;
}

export function findMaxRit(growthGoalYn, previousRitScore) {
    return growthGoalYn ? previousRitScore + CommonValues.MAX_GROWTH : CommonValues.MAX_RIT;
}

export function findMinRit(growthGoalYn, previousRitScore) {
    return growthGoalYn ? previousRitScore + CommonValues.MIN_GROWTH : CommonValues.MIN_RIT;
}

export function getCGIWarning(cgi, growthGoalYn, allValidYn) {
    if (!growthGoalYn || !allValidYn) {
        return null;
    } else if (cgi > 1.0) {
        return CommonStrings.cgiWarning.highGrowth;
    } else if (cgi >= -1.0 && cgi < -0.5) {
        return CommonStrings.cgiWarning.sortaLowGrowth;
    } else if (cgi < -1.0) {
        return CommonStrings.cgiWarning.lowGrowth;
    } else {
        return null;
    }
}

export function findStartingTerm(studentTermData, testEvent) {
    if (!testEvent || !studentTermData) {
        return null;
    }

    return studentTermData.find((t) => t.get('termNumber') === testEvent.get('termNumber'), null, null);
}

export function goalAlreadyExists(existingGoals, newGoal) {
    if (!existingGoals) {
        return false;
    }
    return !!existingGoals.find((g) => g.get('endTermNumber') === newGoal.get('endTermNumber'));
}

export default {
    canSetGrowthGoal,
    findCurrentGoals,
    findEndTermScore,
    findGoalSettingTerms,
    findInitialRitTarget,
    findMaxRit,
    findMaxTestEventForSubject,
    findMaxTestEventForMSID,
    findMinRit,
    findPastGoals,
    findPreviousRitScore,
    findStartingTerm,
    findTypicalGrowthForTargetTerm,
    findTypicalScore,
    getFirstOpenTerm,
    getCGIWarning,
    goalAlreadyExists,
    goalStatus,
    isGrowthGoal,
    testEventAchievingGoal,
};
